import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  SxProps,
  Stack,
  ButtonGroup,
  IconButton,
  Box,
  InputAdornment,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScrollTimePicker from '../scrollPicker/ScrollTimePicker';
import { format } from 'date-fns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SwipeVerticalIcon from '@mui/icons-material/SwipeVertical';
import { useTheme } from '@mui/material/styles';
import { StaticTimePicker } from '@mui/x-date-pickers/StaticTimePicker';

const getDefaultTimePicker = () => {
  const timePicker = localStorage.getItem('timePicker');
  if (timePicker === 'scroll' || timePicker === 'clock') return timePicker;
  return 'scroll';
};

const TimeInput = ({
  value,
  onChange,
  disabled = false,
  setAnchorEl,
  sx = {},
  id,
  showTimeIcon = false,
  'aria-label': ariaLabel,
}: {
  value: Date | null;
  onChange?: (newDate: Date) => void;
  disabled?: boolean;
  setAnchorEl?: (anchorEl: HTMLElement | null) => void;
  sx?: SxProps;
  id?: string;
  showTimeIcon?: boolean;
  'aria-label'?: string;
  openOnKeyboardFocus?: boolean;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [timePickerVariant, setTimePickerVariant] = useState<'clock' | 'scroll'>(
    getDefaultTimePicker(),
  );

  const setNewTimePickerVariant = (newVariant: 'clock' | 'scroll') => {
    setTimePickerVariant(newVariant);
    localStorage.setItem('timePicker', newVariant);
  };

  const [localValue, setLocalValue] = useState<Date | null>(value ? new Date(value) : null);

  const { palette } = useTheme();

  const anchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (anchorRef.current && setAnchorEl) {
      setAnchorEl(anchorRef.current);
    }
  }, [anchorRef]);

  const handleClickOpen = () => {
    if (!disabled) setOpen(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClickOpen();
    }
  };

  const handleClose = () => {
    if (value) setLocalValue(new Date(value));
    else setLocalValue(null);
    setOpen(false);
  };

  const handleOk = () => {
    if (onChange && localValue) onChange(localValue);
    setOpen(false);
  };

  useEffect(() => {
    if (value) setLocalValue(new Date(value));
    else setLocalValue(null);
  }, [value]);

  const onSetTime = (date: Date | null) => {
    if (date) setLocalValue(new Date(date));
  };

  const dateValue = (date: Date | null) => {
    if (date && !isNaN(date.getTime())) return format(date, 'HH:mm');

    return '';
  };

  const handleDialogKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleOk();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleClose();
    }
  };

  const okButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open && okButtonRef.current) {
      setTimeout(() => {
        okButtonRef.current?.focus();
      }, 100);
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        sx={timePickerVariant === 'clock' ? { '& .MuiDialog-paper': { minWidth: '320px' } } : {}}
        onKeyDown={handleDialogKeyDown}
        disableEscapeKeyDown={false}
        keepMounted={false}
      >
        <Stack direction="row" justifyContent="space-between">
          <DialogTitle>{t('Select time')}</DialogTitle>
          <ButtonGroup sx={{ pr: 2, mt: 1 }}>
            <IconButton
              onClick={() => setNewTimePickerVariant('clock')}
              aria-label={String(t('Clock view'))}
              size="medium"
              sx={{
                backgroundColor:
                  timePickerVariant === 'clock' ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                '&:hover': {
                  backgroundColor:
                    timePickerVariant === 'clock'
                      ? 'rgba(25, 118, 210, 0.2)'
                      : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <AccessTimeIcon
                sx={{ color: timePickerVariant === 'clock' ? palette.primary.main : 'lightgray' }}
              />
            </IconButton>
            <IconButton
              onClick={() => setNewTimePickerVariant('scroll')}
              aria-label={String(t('Scroll view'))}
              size="medium"
              sx={{
                backgroundColor:
                  timePickerVariant === 'scroll' ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                '&:hover': {
                  backgroundColor:
                    timePickerVariant === 'scroll'
                      ? 'rgba(25, 118, 210, 0.2)'
                      : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <SwipeVerticalIcon
                sx={{ color: timePickerVariant === 'scroll' ? palette.primary.main : 'lightgray' }}
              />
            </IconButton>
          </ButtonGroup>
        </Stack>
        <DialogContent sx={{ p: 0 }}>
          {timePickerVariant === 'clock' ? (
            <StaticTimePicker
              value={localValue}
              onChange={onSetTime}
              slotProps={{ actionBar: { actions: [] } }}
              displayStaticWrapperAs="mobile"
            />
          ) : (
            <ScrollTimePicker value={localValue} onChange={onSetTime} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('Cancel')}</Button>
          <Button onClick={handleOk} ref={okButtonRef} autoFocus>
            {t('Ok')}
          </Button>
        </DialogActions>
      </Dialog>
      <TextField
        id={id || 'time-input'}
        ref={anchorRef}
        placeholder="hh:mm"
        value={dateValue(value)}
        InputProps={{
          readOnly: true,
          endAdornment: showTimeIcon ? (
            <InputAdornment position="end">
              <AccessTimeIcon color="action" />
            </InputAdornment>
          ) : undefined,
        }}
        onClick={handleClickOpen}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        fullWidth
        inputProps={{
          'aria-label': ariaLabel,
          tabIndex: 0,
        }}
        sx={{
          '& .MuiOutlinedInput-input': {
            cursor: 'pointer',
          },
          ...sx,
        }}
      />
    </>
  );
};

export default TimeInput;
